import { ConditionType, Field, FieldControllerType } from '@gemini/lux-ds-pms';
import { CustomTranslationFnc } from '@hooks/useCustomTranslation';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import {
  CountryListDTO,
  InsuranceDTO,
  MasterLookUpDTO,
} from '@utils/interfaces';

const primaryInsuranceForm = (
  t: CustomTranslationFnc,
  isVision: boolean,
  options?: {
    insurances: InsuranceDTO;
    genderList: MasterLookUpDTO;
    holders: MasterLookUpDTO;
    countryList: CountryListDTO;
    countryStatesList: CountryListDTO;
  },
): Field[] => [
  {
    id: isVision
      ? 'patientDetails.visionInsuranceDetails'
      : 'patientDetails.medicalInsuranceDetails',
    labelKey: isVision
      ? 'fieldsLabel.provideVisionInsuranceDetails'
      : 'fieldsLabel.provideMedicalInsuranceDetails',
    type: FieldControllerType.GROUP,
    visibility: true,
    disabled: false,
    size: {
      xs: 12,
      md: 12,
      lg: 12,
    },
    children: [
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.visionInsurance'
          : 'patientDetails.medicalInsuranceDetails.medicalInsurance',
        labelKey: isVision
          ? 'fieldsLabel.visionInsurance'
          : 'fieldsLabel.medicalInsurance',
        type: FieldControllerType.DROPDOWN,
        visibility: true,
        disabled: true,
        size: {
          xs: 12,
          md: 6,
          lg: 6,
        },
        options: options
          ? options.insurances.map(insurance => ({
              key: insurance.insuranceId.toString(),
              label: insurance.name,
              type: insurance.type,
            }))
          : [],
      },
      {
        // Other è disponibile solo per insurance d tipo Vision
        id: 'patientDetails.visionInsuranceDetails.otherInsuranceName',
        labelKey: 'fieldsLabel.yourInsurance',
        type: FieldControllerType.TEXT,
        visibility: isVision ? true : false,
        disabled: true,
        size: {
          xs: 12,
          md: 6,
          lg: 6,
        },
        visibilityCondition: {
          conditions: isVision
            ? [
                {
                  idRef:
                    'patientDetails.visionInsuranceDetails.visionInsurance',
                  valueRef: StaticValues.OTHER_OPTION,
                },
              ]
            : [],
        },
      },
    ],
  },
  {
    id: isVision
      ? 'patientDetails.visionInsuranceDetails.visionInsuranceMemberId'
      : 'patientDetails.medicalInsuranceDetails.medicalInsuranceMemberId',
    labelKey: isVision
      ? 'fieldsLabel.visionInsuranceMemberId'
      : 'fieldsLabel.medicalInsuranceMemberId',
    type: FieldControllerType.TEXT,
    otherOptions: {
      text: {
        maxLength: 50,
      },
    },
    validations: [
      {
        type: 'regex',
        value: /^[a-zA-Z0-9]+$/,
        errorMsgKey: 'fieldsLabel.errors.onlyLettersAndNumbers',
      },
    ],
    visibility: true,
    disabled: false,
    size: {
      xs: 12,
      md: 6,
      lg: 6,
    },
  },
  {
    id: isVision
      ? 'patientDetails.visionInsuranceDetails.visionInsuranceHolder'
      : 'patientDetails.medicalInsuranceDetails.medicalInsuranceHolder',
    labelKey: isVision
      ? 'fieldsLabel.visionInsuranceHolder'
      : 'fieldsLabel.medicalInsuranceHolder',
    type: FieldControllerType.RADIO,
    visibility: true,
    disabled: true,
    size: {
      xs: 12,
      md: 12,
      lg: 12,
    },
    options: [
      { key: StaticValues.YES, label: 'fieldsLabel.yes' },
      { key: StaticValues.NO, label: 'fieldsLabel.no' },
    ],
  },
  {
    id: isVision
      ? 'patientDetails.visionInsuranceDetails'
      : 'patientDetails.medicalInsuranceDetails',
    labelKey: isVision
      ? 'fieldsLabel.primaryVisionInsuranceHolder'
      : 'fieldsLabel.primaryMedicalInsuranceHolder',
    type: FieldControllerType.GROUP,
    visibility: true,
    disabled: false,
    size: {
      xs: 12,
      md: 12,
      lg: 12,
    },
    visibilityCondition: {
      conditions: [
        {
          idRef: isVision
            ? 'patientDetails.visionInsuranceDetails.visionInsuranceHolder'
            : 'patientDetails.medicalInsuranceDetails.medicalInsuranceHolder',
          valueRef: StaticValues.NO,
        },
      ],
    },
    children: [
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.firstName'
          : 'patientDetails.medicalInsuranceDetails.firstName',
        labelKey: 'fieldsLabel.firstName',
        type: FieldControllerType.TEXT,
        visibility: true,
        disabled: false,
        size: {
          xs: 12,
          md: 6,
          lg: 6,
        },
        validations: [
          {
            type: 'regex',
            value: /^[A-Za-z\s]+$/,
            errorMsgKey: 'fieldsLabel.errors.onlyLetters',
          },
        ],
      },
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.lastName'
          : 'patientDetails.medicalInsuranceDetails.lastName',
        labelKey: 'fieldsLabel.lastName',
        type: FieldControllerType.TEXT,
        visibility: true,
        disabled: false,
        size: {
          xs: 12,
          md: 6,
          lg: 6,
        },
        validations: [
          {
            type: 'regex',
            value: /^[A-Za-z\s]+$/,
            errorMsgKey: 'fieldsLabel.errors.onlyLetters',
          },
        ],
      },
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.gender'
          : 'patientDetails.medicalInsuranceDetails.gender',
        labelKey: 'fieldsLabel.gender',
        type: FieldControllerType.DROPDOWN,
        visibility: true,
        disabled: false,
        size: {
          xs: 12,
          md: 6,
          lg: 6,
        },
        options: options
          ? options.genderList.map(gender => ({
              key: gender.itemId,
              label: gender.itemName,
            }))
          : [],
      },
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.dateOfBirth'
          : 'patientDetails.medicalInsuranceDetails.dateOfBirth',
        labelKey: 'fieldsLabel.dob',
        type: FieldControllerType.DATEPICKER,
        visibility: true,
        disabled: false,
        otherOptions: {
          datePicker: {
            disableFuture: true,
            minDate: new Date('1753-01-01'),
            maxDate: new Date(),
            msgInvalid: t('fieldsLabel.errors.dateFormat'),
            msgMinDate: t('fieldsLabel.errors.dobInvalid'),
            msgMaxDate: t('fieldsLabel.errors.dateInFuture'),
            isMonthPicker: true,
            pickDay: true,
          },
        },
        size: {
          xs: 12,
          md: 6,
          lg: 6,
        },
      },
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.phoneNumber.phoneNumber'
          : 'patientDetails.medicalInsuranceDetails.phoneNumber.phoneNumber',
        labelKey: 'fieldsLabel.phoneNumber',
        otherOptions: {
          phone: {
            prefixId: isVision
              ? 'patientDetails.visionInsuranceDetails.phoneNumber.prefix'
              : 'patientDetails.medicalInsuranceDetails.phoneNumber.prefix',
          },
        },
        // key: 'string', // key per masterLookUp
        type: FieldControllerType.PHONE,
        visibility: true,
        disabled: false,
        size: {
          xs: 12,
          md: 12,
          lg: 8,
        },
        validations: [
          // required gestito da servizio in maniera statica
          {
            type: 'validate',
            value: 0,
            errorMsgKey: 'fieldsLabel.errors.phoneInvalid',
          },
        ],
      },
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.phoneNumber.type'
          : 'patientDetails.medicalInsuranceDetails.phoneNumber.type',
        key: 'string', // key per masterLookUp
        type: FieldControllerType.RADIO,
        visibility: true,
        disabled: false,
        size: {
          xs: 12,
          md: 12,
          lg: 4,
        }, // 6 per metà, 12 intero
        options: [
          { key: 'mobile', label: 'fieldsLabel.mobile' },
          { key: 'landline', label: 'fieldsLabel.landline' },
        ],
        // required gestito da servizio in maniera statica
        interdepCondition: {
          toEvaluateIf: {
            type: 'required',
            value: false,
            errorMsgKey: 'error',
          },
          conditions: [
            {
              idRef: isVision
                ? 'patientDetails.visionInsuranceDetails.phoneNumber.phoneNumber'
                : 'patientDetails.medicalInsuranceDetails.phoneNumber.phoneNumber',
              type: ConditionType.EMPTY,
            },
          ],
        },
      },
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.streetAddress'
          : 'patientDetails.medicalInsuranceDetails.streetAddress',
        labelKey: 'fieldsLabel.streetAddress',
        type: FieldControllerType.TEXT,
        otherOptions: {
          text: {
            maxLength: 55,
          },
        },
        visibility: true,
        disabled: false,
        size: {
          xs: 12,
          md: 6,
          lg: 6,
        },
      },
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.city'
          : 'patientDetails.medicalInsuranceDetails.city',
        labelKey: 'fieldsLabel.city',
        type: FieldControllerType.TEXT,
        otherOptions: {
          text: {
            maxLength: 50,
          },
        },
        validations: [
          {
            type: 'regex',
            value: /^[a-zA-Z0-9'-]+$/,
            errorMsgKey: 'fieldsLabel.errors.noSpecialChars',
          },
        ],
        visibility: true,
        disabled: false,
        size: {
          xs: 12,
          md: 6,
          lg: 6,
        },
      },

      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.zipCode'
          : 'patientDetails.medicalInsuranceDetails.zipCode',
        labelKey: 'fieldsLabel.zipCode',
        type: FieldControllerType.TEXT,
        visibility: true,
        disabled: false,
        size: {
          xs: 6,
          md: 3,
          lg: 3,
        },
        validations: [
          {
            type: 'regex',
            value: /^[0-9]{5}(-[0-9]{4})?$/,
            errorMsgKey: 'fieldsLabel.errors.zipCode',
          },
        ],
      },
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.state'
          : 'patientDetails.medicalInsuranceDetails.state',
        labelKey: 'fieldsLabel.state',
        type: FieldControllerType.DROPDOWN,
        visibility: true,
        disabled: false,
        size: {
          xs: 6,
          md: 3,
          lg: 3,
        },
        options: options
          ? [
              ...options.countryStatesList.map(state => ({
                key: state.code,
                label: state.name,
              })),
            ]
          : [],
      },
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.country'
          : 'patientDetails.medicalInsuranceDetails.country',
        labelKey: 'fieldsLabel.country',
        type: FieldControllerType.DROPDOWN,
        visibility: true,
        disabled: true,
        size: {
          xs: 12,
          md: 6,
          lg: 6,
        },
        options: options
          ? [
              ...options.countryList.map(country => ({
                key: country.code,
                label: country.name,
              })),
            ]
          : [],
      },
      {
        id: isVision
          ? 'patientDetails.visionInsuranceDetails.visionInsuranceHolders'
          : 'patientDetails.medicalInsuranceDetails.medicalInsuranceHolders',
        labelKey: 'fieldsLabel.visionInsuranceHolders',
        type: FieldControllerType.DROPDOWN,
        visibility: true,
        disabled: false,
        size: {
          xs: 12,
          md: 6,
          lg: 6,
        },
        options: options
          ? options.holders
              .filter(
                holder =>
                  holder.itemName !== 'Sibling' && holder.itemName !== 'Friend',
              )
              .map(holder => ({
                key: holder.itemId,
                label: holder.itemName,
              }))
          : [],
      },
    ],
  },
];

export default primaryInsuranceForm;
